import React, { useCallback, useEffect } from "react";
import { Upload, X, File, Image } from "lucide-react";
import { cn } from "../../utils/utils";

export const MyFileUpload = ({
  value = [],
  onChange,
  maxFiles = Number.POSITIVE_INFINITY,
  acceptedFileTypes = "*",
  isMulti = true,
  label,
  labelClassName,
  className,
}) => {
  const [isDragging, setIsDragging] = React.useState(false);
  const [previews, setPreviews] = React.useState([]);

  useEffect(() => {
    const newPreviews = value.map((file) => {
      if (file.type.startsWith("image/")) {
        return URL.createObjectURL(file);
      }
      return null;
    });
    setPreviews(newPreviews);

    // Cleanup function to revoke object URLs
    return () => {
      newPreviews.forEach((preview) => {
        if (preview) URL.revokeObjectURL(preview);
      });
    };
  }, [value]);

  const handleFileChange = useCallback(
    (selectedFiles) => {
      const newFiles = Array.from(selectedFiles);
      let updatedFiles;
      if (isMulti) {
        updatedFiles = [...value, ...newFiles].slice(0, maxFiles);
      } else {
        updatedFiles = newFiles.slice(0, 1);
      }
      onChange(updatedFiles);
    },
    [value, maxFiles, onChange, isMulti],
  );

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      const droppedFiles = Array.from(e.dataTransfer.files);
      handleFileChange(droppedFiles);
    },
    [handleFileChange],
  );

  const removeFile = useCallback(
    (indexToRemove) => {
      const updatedFiles = value.filter((_, index) => index !== indexToRemove);
      onChange(updatedFiles);
    },
    [value, onChange],
  );

  return (
    <div className={cn("w-full", className)}>
      {label ? (
        <label
          className={cn("block font-medium text-gray-700 mb-2", labelClassName)}
        >
          {label}
        </label>
      ) : null}
      <div
        className={`border-2 border-dashed rounded-lg p-4 text-center ${
          isDragging ? "border-blue-500 bg-blue-50" : "border-gray-300"
        }`}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          type="file"
          multiple={isMulti}
          accept={acceptedFileTypes}
          onChange={(e) => {
            handleFileChange(e.target.files);
            e.target.value = null;
          }}
          className="hidden"
          id="fileInput"
        />
        <label
          htmlFor="fileInput"
          className="cursor-pointer flex flex-col items-center justify-center py-6"
        >
          <Upload className="w-12 h-12 text-gray-400" />
          <p className="mt-2 text-gray-500">
            <span className="font-semibold">Click to upload</span> or drag and
            drop
          </p>
          <p className="mt-1 text-xs text-gray-500">
            {acceptedFileTypes !== "*"
              ? `Accepted file types: ${acceptedFileTypes}`
              : "Any file type accepted"}
          </p>
          <p className="mt-1 text-xs text-gray-500">
            {isMulti ? `Up to ${maxFiles} files` : "Single file upload"}
          </p>
        </label>
      </div>
      {value.length > 0 && (
        <ul className="mt-4 space-y-2">
          {value.map((file, index) => (
            <li
              key={index}
              className="flex items-center justify-between bg-gray-100 p-2 rounded"
            >
              <div className="flex items-center space-x-2">
                {previews[index] ? (
                  <img
                    src={previews[index] || "/placeholder.svg"}
                    alt={file.name}
                    className="w-10 h-10 object-cover rounded"
                  />
                ) : file.type.startsWith("image/") ? (
                  <Image className="w-10 h-10 text-gray-400" />
                ) : (
                  <File className="w-10 h-10 text-gray-400" />
                )}
                <span className="truncate">{file.name}</span>
              </div>
              <button
                onClick={() => removeFile(index)}
                className="text-red-500 hover:text-red-700"
              >
                <X className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
